// React Hooks
import { useEffect, useState, useContext, useRef, DragEvent } from "react";
import { Provider } from "../chat.type";
// Data
import { useAppDispatch, useAppSelector } from "core/store/reduxStore";
import {
  setUserInput,
  addToConversation,
  setAiMessageStatus,
  updateMessage,
} from "modules/chat/store/conversationList";
import { SelectChangeEvent } from "@mui/material";

// UI
import { ScrollButtonFab, StyledDownIcon } from "../chatButton.styled";
import { AuthContext } from "core/contexts/auth/auth";
import { PlaceholdersAndVanishInput } from "core/components/global/input/inputBars/imageVanishInputMui";
import { Prompt, MessageType, Form } from "../chat.type";
import { suggestedPrompts } from "./specialCases/data/suggestedPromptData";
import { UserInputContainer } from "../chat.styled";
import { v4 as uuidv4 } from "uuid";
import { Language } from "./specialCases/language";
import { SuggestedPrompts } from "./specialCases/suggestedPrompts";
import { SuggestedPromptForm } from "./specialCases/suggestedPromptForm";
import { object } from "prop-types";
import { uploadFile } from "modules/chat/services/chatService";

export function UserInput({ selectedProvider, socket, isMenuOpen,onExcelUpload }) {
  // Set state for the form input

  const [file, setFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>("#");

  // Get a ref to the Redux dispatcher function
  const dispatch = useAppDispatch();
  const convoState = useAppSelector((state) => state.chatConversations);
  const { checkAuthToken, amsUser } = useContext(AuthContext);

  const [modelSuggestedPrompts, setModelSuggestedPrompts] = useState<Prompt[]>(
    []
  );
  const [modelSuggestedPrompt, setModelSuggestedPrompt] = useState<string | null>(null);
  const [activeForm, setActiveForm] = useState<Form | null>(null);
  const [currentResponse, setCurrentResponse] = useState<string>("");
  const [isStreamComplete, setIsStreamComplete] = useState<boolean>(true);
  const timeoutRef = useRef<number | null>(null);
  const currentChatIdRef = useRef<string | null>(null);

  function generateMessage(chatid, currentMessage, conversationId, isNew) {
    const currentTime = new Date().toISOString();
    return {
      newMessage: {
        chatid: chatid,
        _id: chatid.split(":")[1],
        role: "assistant",
        content: currentMessage,
        model: selectedProvider,
        createdAt: currentTime,
        updatedAt: currentTime,
      } as MessageType,
      conversationId: conversationId,
      isNew: isNew,
    };
  }

  useEffect(() => {
    return () => {
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleResponseComplete = async (fullResponse: string) => {
    // Set the currently aggregated message to an empty string
    setCurrentResponse("");
    // Reset the completeness for the next stream
    setIsStreamComplete(true);
  };

  const resetTimeout = () => {
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = window.setTimeout(() => {
      if (!isStreamComplete) {
        handleResponseComplete(currentResponse);
      }
    }, 2000);
  };

  useEffect(() => {
    if (socket) {
      let uploadCompleteTimeout: NodeJS.Timeout | null = null;

      const onMessage = (messageData: string) => {
        try {
          const parsedData = JSON.parse(messageData);

          // Check if this is an error message
          const isErrorMessage =
            parsedData.content.includes("Unable to process your request") ||
            !parsedData.chatid.includes(":") ||
            parsedData.content.includes(
              "Something went wrong, please try logging in again"
            );

          // Only handle a new message stream if chatId is different and not already handled
          if (
            parsedData.chatid &&
            currentChatIdRef.current !== parsedData.chatid
          ) {
            if (isErrorMessage) {
              
              // Set the AI message status to failed
              dispatch(setAiMessageStatus("failed"));

              // For error messages, we still want to display them to the user
              const errorInput = generateMessage(
                parsedData.chatid,
                parsedData.content,
                parsedData.conversationid,
                true
              );
              dispatch(updateMessage(errorInput));

              // Update the current chat ID even for error messages
              currentChatIdRef.current = parsedData.chatid;
            } else {

              const input = generateMessage(
                parsedData.chatid,
                parsedData.content,
                parsedData.conversationid,
                true
              );
              dispatch(updateMessage(input));
              
              // Check for file upload progress
              if (parsedData.content.toLowerCase().includes("uploaded segment")) {

                // Extract segment information
                const segments = parsedData.content.match(/(\d+) of (\d+)/);
                if (segments && segments.length === 3) {
                  const currentSegment = parseInt(segments[1]);
                  const totalSegments = parseInt(segments[2]);

                  // If it's the last segment, start a timeout
                  if (currentSegment === totalSegments) {
                    uploadCompleteTimeout = setTimeout(() => {
                      console.warn("Upload complete but no response within 3 seconds.");
                      dispatch(setAiMessageStatus("failed")); 
                    }, 3000); 
                  }
                }
              } else {
                // Clear the timeout if it's not a segment upload message
                if (uploadCompleteTimeout) {
                  clearTimeout(uploadCompleteTimeout);
                  uploadCompleteTimeout = null;
                }

                // Turn off the loading animation for successful messages
                dispatch(setAiMessageStatus("succeeded"));

                // A new 'currentChatIdRef.current' denotes a new 'assistant' UI block
                currentChatIdRef.current = parsedData.chatid;

                // Set the stream to in-progress
                setIsStreamComplete(false);
              }
            }
          } else {
            // Clear the timeout if it's not a new chatId 
            if (uploadCompleteTimeout) {
              clearTimeout(uploadCompleteTimeout);
              uploadCompleteTimeout = null;
            }

            // Update the CL Redux state with the updated 'assistant' string
            const input = generateMessage(
              parsedData.chatid,
              parsedData.content,
              parsedData.conversationid,
              false
            );
            dispatch(updateMessage(input));
          } 
        } catch (e) {
          dispatch(setAiMessageStatus("failed"));
          console.error("Error parsing message:", e);
        }
      }; 

      socket.on("message", onMessage);
      socket.on("file_chunk", onMessage); 

      return () => {
        // Clean up listeners when the component unmounts
        socket.off("message", onMessage);
        socket.off("file_chunk", onMessage);
      };
    }
  }, [socket, dispatch]); 

  /**
   * Send the user prompt and return a response (async as sendPrompt() has a Promise)
   * Set the initial and return in the Redux Messages slice as the in focus messages
   * The useEffect below will handle updating the conversations list with this new data
   */
  
  

  const sendPromptToApi = async (value) => {
    if (value.length > 5000) {
    alert("Input exceeds 100000 characters limit.");
    return;
  }
    const token = await checkAuthToken();
  
    // Handle text input
    if (selectedProvider.name === "Translate") {
      value = CustomizedUserInput(value);
    }
    // Check if the value is a file
    if (value instanceof File) {
      // Dispatch the file to the conversation
      dispatch(setUserInput(value));
      dispatch(
        addToConversation({
          accessToken: token,
          userPrompt: value,
          userEmail: amsUser?.email || "",
          socket: socket,
        })
      );
    } else if (value !== "" && value !== undefined) {
      // If it's a text input, proceed as normal
      dispatch(setUserInput(value));
      dispatch(
        addToConversation({
          accessToken: token,
          userPrompt: value,
          userEmail: amsUser?.email || "",
          socket: socket,
        })
      );
    }
  };
  
  

  const handleAnyFileUploadFunction = async (event) => {
    // Extract the file from the event
    const files = event.dataTransfer.files;

    // Only care for if a file exists
    if (files.length > 0) {
      // Obtain the token, redirect to /logout on expiration
      const token = await checkAuthToken();

      // Select the first: not currently supporting multiple uploads
      const file = files[0];

      // Add the user Message
      dispatch(
        addToConversation({
          accessToken: token,
          userPrompt: file,
          userEmail: amsUser?.email || "",
          socket: socket,
        })
      );
    }
  };

  const handleInputSelect = async (value: string) => {
    const token = await checkAuthToken();

    if (value !== "" && value !== undefined) {
      dispatch(setUserInput(value));

      dispatch(
        addToConversation({
          accessToken: token,
          userPrompt: value,
          userEmail: amsUser?.email || "",
          socket: socket,
        })
      );
    }
  };

  const handleFileDrop = async (event) => {
    event.preventDefault();
    handleAnyFileUploadFunction(event);

    // Extract the file from the event
    const files = event.dataTransfer.files;
    if (files.length > 0) {
      // Select the first: not currently supporting multiple uploads
      const file = files[0];
      if (file) {
        setImagePreview(URL.createObjectURL(file));
      }
    }
  };

  /**
   * Update the model prompts if found in ./specialCases/data/suggestedPromptData.tsx
   */
  useEffect(() => {
    setModelSuggestedPrompt("");
    
    if (typeof selectedProvider === "object" && selectedProvider !== null) {
      // Find suggested prompts for the pinned model
      const foundPrompts = suggestedPrompts.find(
        (item) => item.model === selectedProvider.name
      )?.prompts;

      // Update modelSuggestedPrompts if prompts are found
      if (foundPrompts) {
        setModelSuggestedPrompts(foundPrompts);
        
      } else {
        setModelSuggestedPrompts([]);
      }
    }
  }, [selectedProvider]);

  const [inputLanguage, setInputLanguage] = useState("");
  const [outputLanguage, setOutputLanguage] = useState("");
  const handleChangeInputLanguage = (event: SelectChangeEvent) => {
    setInputLanguage(event.target.value as string);
  };
  const handleChangeOutputLanguage = (event: SelectChangeEvent) => {
    setOutputLanguage(event.target.value as string);
  };

  const CustomizedUserInput = (value) => {
    if (
      inputLanguage === "" ||
      outputLanguage === "" ||
      inputLanguage === outputLanguage
    ) {
      return;
    }

    const payload = [
      {
        input_language: inputLanguage,
        output_language: outputLanguage,
        text: value,
        id: uuidv4(),
      },
    ];

    const jsonPayload = JSON.stringify(payload);
    return jsonPayload;
  };

  return (
    
    <UserInputContainer className="w-100">
      {/* List of Suggested Prompts */}
      <SuggestedPrompts
        modelSuggestedPrompts={modelSuggestedPrompts}
        setActiveForm={setActiveForm}
        setModelSuggestedPrompt={setModelSuggestedPrompt}
      />

      {/* Form Input for Suggested Prompts */}
      {activeForm && (
        <SuggestedPromptForm
          activeForm={activeForm}
          setActiveForm={setActiveForm}
          handleInputSelect={handleInputSelect}
          socket={socket}
        />
      )}

      {/* Language Picker */}
      {selectedProvider && selectedProvider?.name === "Translate" && (
        <Language
          isMenuOpen={isMenuOpen}
          inputLanguage={inputLanguage}
          setInputLanguage={setInputLanguage}
          outputLanguage={outputLanguage}
          setOutputLanguage={setOutputLanguage}
          handleChangeInputLanguage={handleChangeInputLanguage}
          handleChangeOutputLanguage={handleChangeOutputLanguage}
        />
      )}

      <PlaceholdersAndVanishInput
        placeholders={modelSuggestedPrompts}
        handlePromptSubmit={sendPromptToApi}
        isWaitingForResponse={convoState.loadingAiResp}
        isWide={isMenuOpen}
        suggestedPrompt={modelSuggestedPrompt}
        onExcelUpload={onExcelUpload}
        providerName={
          selectedProvider !== undefined ? selectedProvider.name : ""
        }
        acceptsText={
          selectedProvider !== undefined
            ? selectedProvider.options.accepts_text
            : false
        }
        acceptsFiles={
          selectedProvider !== undefined
            ? selectedProvider.options.accepts_files
            : false
        }
      />
    </UserInputContainer>
  );
}
